import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageIcon from '@mui/icons-material/Image';
import { isHtmlContent, normalizeListWrappers, plainTextToHtml } from '../../utils/richText';
import { ImageUploadService } from '../accounting/inventory/services/imageUploadService';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
}

const COLUMN_WRAPPER_CLASS = 'rt-columns';

/**
 * Hafif, bağımlılıksız zengin metin editörü.
 * contentEditable + execCommand kullanır; çıktı HTML string olarak saklanır.
 * Paragraf, madde/numaralı liste, kalın, görsel, link, hizalama ve sütun düzeni destekler.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  minHeight = 140,
  disabled = false,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const isFocusedRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkText, setLinkText] = useState('');
  const [hasLinkSelection, setHasLinkSelection] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  const normalizedIncoming = useMemo(() => {
    if (!value) return '';
    return isHtmlContent(value) ? value : plainTextToHtml(value);
  }, [value]);

  // Dışarıdan gelen değeri, editör odakta değilken DOM'a yansıt.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isFocusedRef.current) return;
    if (el.innerHTML !== normalizedIncoming) {
      el.innerHTML = normalizedIncoming;
    }
  }, [normalizedIncoming]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    onChangeRef.current(normalizeListWrappers(el.innerHTML));
  }, []);

  useEffect(
    () => () => {
      const el = editorRef.current;
      if (!el) return;
      onChangeRef.current(normalizeListWrappers(el.innerHTML));
    },
    []
  );

  // Kaynak stillerini (ör. ChatGPT koyu arka plan / beyaz yazı) alma —
  // metni düz olarak yapıştır. Panodaki görselleri yükleyip ekle.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      const items = Array.from(e.clipboardData.items || []);
      const imageItem = items.find((item) => item.type.startsWith('image/'));
      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (!file) return;
        void (async () => {
          try {
            const [uploaded] = await ImageUploadService.uploadImages([file]);
            if (!uploaded?.originalUrl) return;
            const safeSrc = uploaded.originalUrl.replace(/"/g, '');
            document.execCommand('insertHTML', false, `<img src="${safeSrc}" alt="" />`);
            emitChange();
          } catch (error) {
            console.error('Pasted image upload failed:', error);
          }
        })();
        return;
      }
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      document.execCommand('insertText', false, text);
      emitChange();
    },
    [disabled, emitChange]
  );

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = editorRef.current;
      if (!el || disabled) return;
      el.focus();

      // Hizalama komutlarında seçim yoksa / kısmiyse tüm içeriği seç —
      // aksi halde yalnızca imlecin olduğu paragraf hizalanır.
      const isAlignCommand =
        command === 'justifyLeft' ||
        command === 'justifyCenter' ||
        command === 'justifyRight' ||
        command === 'justifyFull';
      if (isAlignCommand) {
        const sel = window.getSelection();
        const hasSelectionInside =
          !!sel &&
          !sel.isCollapsed &&
          !!sel.anchorNode &&
          el.contains(sel.anchorNode) &&
          (!sel.focusNode || el.contains(sel.focusNode));
        if (!hasSelectionInside && sel) {
          const range = document.createRange();
          range.selectNodeContents(el);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      document.execCommand(command, false, arg);
      emitChange();
    },
    [disabled, emitChange]
  );

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    if (savedRangeRef.current && el.contains(savedRangeRef.current.commonAncestorContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  }, []);

  const markAnchorsSafe = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.querySelectorAll('a[href]').forEach((anchor) => {
      const href = (anchor.getAttribute('href') || '').trim();
      if (!/^(https?:|mailto:|tel:|\/)/i.test(href)) return;
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
  }, []);

  const openLinkDialog = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    el.focus();
    saveSelection();

    const sel = window.getSelection();
    const selectedText = sel && !sel.isCollapsed ? sel.toString() : '';
    setHasLinkSelection(Boolean(selectedText));
    setLinkText(selectedText);
    setLinkUrl('https://');
    setLinkDialogOpen(true);
  }, [disabled, saveSelection]);

  const applyLink = useCallback(() => {
    const url = linkUrl.trim();
    if (!url || !/^(https?:|mailto:|tel:|\/)/i.test(url)) return;

    const safeUrl = url.replace(/"/g, '');
    setLinkDialogOpen(false);
    requestAnimationFrame(() => {
      restoreSelection();
      if (hasLinkSelection) {
        document.execCommand('createLink', false, safeUrl);
      } else {
        const label = (linkText || safeUrl)
          .trim()
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        document.execCommand('insertHTML', false, `<a href="${safeUrl}">${label}</a>`);
      }
      markAnchorsSafe();
      emitChange();
    });
  }, [emitChange, hasLinkSelection, linkText, linkUrl, markAnchorsSafe, restoreSelection]);

  const removeLink = useCallback(() => {
    exec('unlink');
  }, [exec]);

  const openImageDialog = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    el.focus();
    saveSelection();
    setImageUrl('');
    setImageAlt('');
    setImageUploadError(null);
    setImageDialogOpen(true);
  }, [disabled, saveSelection]);

  const insertImageHtml = useCallback(
    (src: string, alt: string) => {
      const safeSrc = src.trim().replace(/"/g, '');
      if (!safeSrc || !/^(https?:|data:image\/|\/)/i.test(safeSrc)) return false;
      const safeAlt = alt.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      restoreSelection();
      document.execCommand(
        'insertHTML',
        false,
        `<img src="${safeSrc}" alt="${safeAlt}" />`
      );
      emitChange();
      return true;
    },
    [emitChange, restoreSelection]
  );

  const applyImage = useCallback(() => {
    const url = imageUrl.trim();
    if (!url || !/^(https?:|data:image\/|\/)/i.test(url)) return;
    setImageDialogOpen(false);
    requestAnimationFrame(() => {
      insertImageHtml(url, imageAlt);
      setImageUrl('');
      setImageAlt('');
    });
  }, [imageAlt, imageUrl, insertImageHtml]);

  const handleImageFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setImageUploadError('Sadece resim dosyası yükleyebilirsiniz.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setImageUploadError('Dosya boyutu 5MB\'dan küçük olmalıdır.');
        return;
      }
      try {
        setImageUploading(true);
        setImageUploadError(null);
        const [uploaded] = await ImageUploadService.uploadImages([file]);
        if (!uploaded?.originalUrl) {
          throw new Error('Görsel yüklemesi boş URL döndürdü.');
        }
        setImageUrl(uploaded.originalUrl);
        if (!imageAlt.trim()) {
          setImageAlt(file.name.replace(/\.[^.]+$/, ''));
        }
      } catch (error) {
        setImageUploadError(
          error instanceof Error && error.message
            ? error.message
            : 'Görsel yüklenirken bir hata oluştu.'
        );
      } finally {
        setImageUploading(false);
      }
    },
    [imageAlt]
  );

  const setColumns = useCallback(
    (count: number) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();

      const existing = el.querySelector(`.${COLUMN_WRAPPER_CLASS}`) as HTMLElement | null;

      if (count <= 1) {
        // Sütun düzenini kaldır: içeriği dışarı taşı.
        if (existing) {
          while (existing.firstChild) {
            el.insertBefore(existing.firstChild, existing);
          }
          existing.remove();
        }
      } else if (existing) {
        existing.style.columnCount = String(count);
        existing.style.columnGap = '40px';
      } else {
        const wrapper = document.createElement('div');
        wrapper.className = COLUMN_WRAPPER_CLASS;
        wrapper.style.columnCount = String(count);
        wrapper.style.columnGap = '40px';
        while (el.firstChild) {
          wrapper.appendChild(el.firstChild);
        }
        el.appendChild(wrapper);
      }
      emitChange();
    },
    [emitChange]
  );

  const toolbarButton = (
    title: string,
    icon: React.ReactNode,
    onClick: () => void
  ) => (
    <Tooltip title={title} key={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          // mousedown yerine onMouseDown ile seçim kaybını engelle
          onMouseDown={(e) => {
            e.preventDefault();
            if (!disabled) onClick();
          }}
          sx={{ borderRadius: 1 }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          '&:focus-within': { borderColor: 'primary.main', boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}` },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            backgroundColor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {toolbarButton('Kalın', <FormatBoldIcon fontSize="small" />, () => exec('bold'))}
          {toolbarButton('İtalik', <FormatItalicIcon fontSize="small" />, () => exec('italic'))}
          {toolbarButton('Altı çizili', <FormatUnderlinedIcon fontSize="small" />, () => exec('underline'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Paragraf', <Box sx={{ fontSize: 11, fontWeight: 700, px: 0.25 }}>P</Box>, () => exec('formatBlock', 'p'))}
          {toolbarButton('Başlık', <Box sx={{ fontSize: 11, fontWeight: 700, px: 0.25 }}>H2</Box>, () => exec('formatBlock', 'h2'))}
          {toolbarButton('Alt başlık', <Box sx={{ fontSize: 11, fontWeight: 700, px: 0.25 }}>H3</Box>, () => exec('formatBlock', 'h3'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Madde işaretli liste', <FormatListBulletedIcon fontSize="small" />, () => exec('insertUnorderedList'))}
          {toolbarButton('Numaralı liste', <FormatListNumberedIcon fontSize="small" />, () => exec('insertOrderedList'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Link ekle', <LinkIcon fontSize="small" />, openLinkDialog)}
          {toolbarButton('Linki kaldır', <LinkOffIcon fontSize="small" />, removeLink)}
          {toolbarButton('Görsel ekle', <ImageIcon fontSize="small" />, openImageDialog)}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Sola hizala', <FormatAlignLeftIcon fontSize="small" />, () => exec('justifyLeft'))}
          {toolbarButton('Ortala', <FormatAlignCenterIcon fontSize="small" />, () => exec('justifyCenter'))}
          {toolbarButton('Sağa hizala', <FormatAlignRightIcon fontSize="small" />, () => exec('justifyRight'))}
          {toolbarButton('İki yana yasla', <FormatAlignJustifyIcon fontSize="small" />, () => exec('justifyFull'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Tek sütun', <ViewStreamIcon fontSize="small" />, () => setColumns(1))}
          {toolbarButton('İki sütun', <ViewColumnIcon fontSize="small" />, () => setColumns(2))}
          {toolbarButton('Üç sütun', <ViewWeekIcon fontSize="small" />, () => setColumns(3))}
        </Box>

        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emitChange}
          onPaste={handlePaste}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            emitChange();
          }}
          data-placeholder={placeholder || ''}
          sx={{
            minHeight,
            px: 1.5,
            py: 1,
            outline: 'none',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
            '& p': { margin: '0 0 0.75em' },
            '& h2': { fontSize: '1.2rem', fontWeight: 700, margin: '0.85em 0 0.4em' },
            '& h3': { fontSize: '1.05rem', fontWeight: 600, margin: '0.7em 0 0.35em' },
            '& ul, & ol': { margin: '0 0 0.75em', paddingLeft: '1.5em' },
            '& a': { color: 'primary.main', textDecoration: 'underline' },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, margin: '8px 0', display: 'block' },
            '& .rt-columns': { columnGap: '40px' },
            '&:empty:before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
            },
          }}
        />
      </Box>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Ekle</DialogTitle>
        <DialogContent>
          {!hasLinkSelection && (
            <TextField
              autoFocus
              margin="dense"
              label="Link metni"
              fullWidth
              variant="outlined"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            autoFocus={hasLinkSelection}
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>İptal</Button>
          <Button
            onClick={applyLink}
            variant="contained"
            disabled={
              !/^(https?:|mailto:|tel:|\/)/i.test(linkUrl.trim()) ||
              (!hasLinkSelection && !linkText.trim())
            }
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageDialogOpen} onClose={() => !imageUploading && setImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Görsel Ekle</DialogTitle>
        <DialogContent>
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            disabled={imageUploading}
            onClick={() => imageFileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            {imageUploading ? 'Yükleniyor...' : 'Bilgisayardan yükle'}
          </Button>
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageFileChange}
          />
          {imageUploadError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
              {imageUploadError}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Görsel URL"
            fullWidth
            variant="outlined"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/gorsel.jpg"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Alternatif metin (opsiyonel)"
            fullWidth
            variant="outlined"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)} disabled={imageUploading}>İptal</Button>
          <Button
            onClick={applyImage}
            variant="contained"
            disabled={imageUploading || !/^(https?:|data:image\/|\/)/i.test(imageUrl.trim())}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor;
